import type { MetadataVariable, MetadataVariableFormatEnum } from "@/generated/sdk"
import { DqdlRule } from "./types"

/**
 * Maps a non-text declared Format to the Spark type its values must be castable to. ColumnDataType
 * checks castability on the string-read frame, so a declared-numeric column holding "BL" fails as a
 * row-level error. Integer probes as LONG so a fractional value fails; Decimal and Double probe as
 * DOUBLE (Decimal is not a valid ColumnDataType target; Double-castability is the
 * numeric-parseability check). Char has no entry: every value is already text.
 */
const FORMAT_TO_DATA_TYPE: Partial<Record<MetadataVariableFormatEnum, string>> = {
  Integer: "LONG",
  Decimal: "DOUBLE",
  Double: "DOUBLE",
  Date: "DATE",
  Datetime: "TIMESTAMP",
}

/**
 * Returns the ColumnDataType target for a declared Format, or undefined for text (Char) or
 * unspecified formats. Text and typed formats are mutually exclusive.
 */
const dataTypeForFormat = (format: MetadataVariableFormatEnum | undefined): string | undefined => {
  if (!format) return undefined // Assume text if not specified
  return FORMAT_TO_DATA_TYPE[format]
}

/**
 * Checks if a format indicates a text type. ColumnLength only works on StringType columns, so it is
 * skipped for any format that yields a ColumnDataType rule.
 */
const isStringFormat = (format: MetadataVariableFormatEnum | undefined): boolean =>
  dataTypeForFormat(format) === undefined

/**
 * Derives DQDL rules from metadata variables defined in the Metadata tab.
 *
 * Generates:
 *
 * - IsPrimaryKey rule from columns marked as unique_identifier
 * - IsComplete rules from columns marked as populate_for_all
 * - ColumnLength rules from columns with max_length specified (string columns only)
 * - ColumnDataType rules from columns with a non-text declared Format (numeric/Date/Datetime)
 *
 * @param variables - Array of metadata variables from transfer_metadata_schema.
 * @returns Read-only derived rules, ordered by family (IsPrimaryKey, IsComplete, ColumnLength,
 *   ColumnDataType). Mirrors the backend's derive_rules_from_metadata.
 */
export function deriveRulesFromMetadata(variables: MetadataVariable[] | undefined): DqdlRule[] {
  if (!variables?.length) return []

  const rules: DqdlRule[] = []

  // IsPrimaryKey combining every column marked as unique_identifier
  const pkColumns = variables
    .filter((v) => v.unique_identifier && v.variable_name)
    .map((v) => v.variable_name!)
  if (pkColumns.length) rules.push({ type: "IsPrimaryKey", columns: pkColumns })

  // IsComplete for each column marked as populate_for_all
  for (const v of variables) {
    if (v.populate_for_all && v.variable_name) {
      rules.push({ type: "IsComplete", column: v.variable_name })
    }
  }

  // ColumnLength for each text column with a max_length (string columns only).
  // The Metadata editor writes max_length from a text input, so unsaved content can carry a
  // string here even though the persisted/SDK type is a number - coerce and skip if not numeric.
  for (const v of variables) {
    if (v.max_length != null && v.variable_name && isStringFormat(v.format)) {
      const max = Number(v.max_length)
      if (Number.isFinite(max)) {
        rules.push({ type: "ColumnLength", column: v.variable_name, max })
      }
    }
  }

  // ColumnDataType verifies the declared Format against the data. Mutually
  // exclusive with ColumnLength: a typed column never also gets a length rule.
  for (const v of variables) {
    const dataType = dataTypeForFormat(v.format)
    if (dataType && v.variable_name) {
      rules.push({ type: "ColumnDataType", column: v.variable_name, dataType })
    }
  }

  return rules
}
